import { Component, inject, input, signal, viewChild } from '@angular/core';
import {
  ConfirmService,
  SimpleDialog,
  SimpleDialogLayout,
  InputLabel,
  Button,
  Textarea,
  RadioGroup,
  Radio,
} from 'excellence-ui';
import { Api } from '../../../../core/clients/omni-core/api';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { form, FormField, required } from '@angular/forms/signals';
import { HttpError } from '../../../../core/clients/omni-core/models';
import { createConsultantRequest } from '../../../../core/clients/omni-core/functions';
import { FieldErrors } from '../../../../shared/components/field-errors/field-errors';
import { HttpErrorComponent } from '../../../../shared/components/http-error/http-error';
import { getEmptyForm } from './definitions';

@Component({
  selector: 'app-supplier-contract-contact-button',
  imports: [
    SimpleDialog,
    SimpleDialogLayout,
    InputLabel,
    TranslocoPipe,
    FieldErrors,
    FormField,
    HttpErrorComponent,
    Button,
    Textarea,
    RadioGroup,
    Radio,
  ],
  templateUrl: './supplier-contract-contact-button.html',
})
export class SupplierContractContactButton {
  private api = inject(Api);
  private confirmService = inject(ConfirmService);
  private translocoService = inject(TranslocoService);

  organizationId = input.required<string>();

  private dialog = viewChild(SimpleDialog);

  private contactData = signal(getEmptyForm());

  protected contactForm = form(this.contactData, (schemaPath) => {
    required(schemaPath.subject);
    required(schemaPath.message);
  });

  protected sendContactEmailState = signal<{
    isLoading: boolean;
    error: HttpError | unknown | null;
  }>({
    isLoading: false,
    error: null,
  });

  protected onSubmit() {
    if (this.sendContactEmailState().isLoading || !this.contactForm().valid()) {
      this.contactForm.subject().markAsTouched();
      this.contactForm.message().markAsTouched();
      return;
    }
    this.sendContactEmail();
  }

  private async sendContactEmail() {
    this.sendContactEmailState.set({
      isLoading: true,
      error: null,
    });
    const data = this.contactForm().value();
    const topic =
      data.subject === 'HAS_CONTRACTS'
        ? this.translocoService.translate(
            'cockpit.supplierContracts.contactButton.dialog.subject.hasContracts.label',
          )
        : this.translocoService.translate(
            'cockpit.supplierContracts.contactButton.dialog.subject.noContracts.label',
          );
    try {
      await this.api.invoke(createConsultantRequest, {
        oid: this.organizationId(),
        body: {
          content: data.message,
          topic,
        },
      });
      this.sendContactEmailState.set({
        isLoading: false,
        error: null,
      });
      this.onSendContactEmailSuccess();
    } catch (error) {
      this.sendContactEmailState.set({
        isLoading: false,
        error,
      });
    }
  }

  private onSendContactEmailSuccess() {
    this.dialog()?.close();
    this.confirmService.confirm({
      title: this.translocoService.translate(
        'cockpit.supplierContracts.contactButton.success.title',
      ),
      message: this.translocoService.translate(
        'cockpit.supplierContracts.contactButton.success.message',
      ),
      allowCancel: false,
      confirmButtonText: this.translocoService.translate(
        'cockpit.supplierContracts.contactButton.success.confirm',
      ),
      icon: 'heroInformationCircle',
    });
    this.contactData.set(getEmptyForm());
    this.contactForm().reset();
  }
}
